import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "title", "inputStep", "codeStep", "contactInput", "inputLabel",
    "codeInput", "codeHint", "sendButton", "verifyButton",
    "message", "messageText"
  ]

  static values = {
    sendOtpUrl: String,
    verifyOtpUrl: String,
    updateContactUrl: String,
    customerPermalink: { type: String, default: "" }
  }

  connect() {
    this.challengeToken = null
    this.contactType = null
    this.showReloadNotice()
  }

  openModal(event) {
    this.contactType = event.params.contactType

    // Set up the modal based on contact type
    if (this.contactType === "email") {
      this.titleTarget.textContent = this.getTranslation("add_email")
      this.inputLabelTarget.textContent = this.getTranslation("enter_email")
      this.contactInputTarget.type = "email"
      this.contactInputTarget.placeholder = "email@example.com"
    } else {
      this.titleTarget.textContent = this.getTranslation("add_phone")
      this.inputLabelTarget.textContent = this.getTranslation("enter_phone")
      this.contactInputTarget.type = "tel"
      this.contactInputTarget.placeholder = "+48123456789"
    }

    // Reset form
    this.contactInputTarget.value = ""
    this.codeInputTarget.value = ""
    this.inputStepTarget.classList.remove("hidden")
    this.codeStepTarget.classList.add("hidden")
    this.hideMessage()

    const modal = document.getElementById("contact-update-modal")
    modal.showModal()
  }

  async sendCode() {
    const contact = this.contactInputTarget.value.trim()
    if (!contact) {
      this.showError(this.contactType === "email" ? "Please enter your email" : "Please enter your phone number")
      return
    }

    this.setLoading(this.sendButtonTarget, true)

    try {
      const payload = { login: contact }
      if (this.customerPermalinkValue) payload.customer_permalink = this.customerPermalinkValue

      const response = await fetch(this.sendOtpUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Unable to send code.")

      this.challengeToken = data.challenge_token

      // Show code verification step
      this.inputStepTarget.classList.add("hidden")
      this.codeStepTarget.classList.remove("hidden")

      const contactType = this.contactType === "email" ? "email" : "SMS"
      this.codeHintTarget.textContent = this.getTranslation("code_sent").replace("%{type}", contactType)
      this.showSuccess(this.getTranslation("code_sent").replace("%{type}", contactType))
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.setLoading(this.sendButtonTarget, false)
    }
  }

  async verifyCode() {
    if (!this.challengeToken) {
      this.showError("Missing challenge token. Please request a new code.")
      return
    }

    const code = this.codeInputTarget.value.trim()
    if (!code) {
      this.showError("Enter the code you received.")
      return
    }

    this.setLoading(this.verifyButtonTarget, true)

    try {
      // First verify the OTP code
      const verifyResponse = await fetch(this.verifyOtpUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          challenge_token: this.challengeToken,
          code: code
        })
      })

      const verifyData = await verifyResponse.json()
      if (!verifyResponse.ok) throw new Error(verifyData.error || "Invalid code.")

      this.showSuccess(this.getTranslation("code_verified"))

      // Now update the contact information
      const updateResponse = await fetch(this.updateContactUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          challenge_token: this.challengeToken
        })
      })

      const updateData = await updateResponse.json()
      if (!updateResponse.ok) throw new Error(updateData.error || "Failed to update contact information.")

      // Close modal and reload page with success parameter
      const modal = document.getElementById("contact-update-modal")
      modal.close()
      window.location.href = window.location.pathname + "?contact_updated=true"
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.setLoading(this.verifyButtonTarget, false)
    }
  }

  // Show notice after page reload
  showReloadNotice() {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('contact_updated') === 'true') {
      const notice = document.createElement('div')
      notice.className = 'toast toast-top toast-center'
      notice.innerHTML = `
        <div class="alert alert-success">
          <span>${document.documentElement.lang === "pl" ? "Dane kontaktowe zostały pomyślnie zaktualizowane!" : "Contact information updated successfully!"}</span>
        </div>
      `
      document.body.appendChild(notice)

      // Remove notice after 3 seconds
      setTimeout(() => notice.remove(), 3000)

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname)
    }
  }

  setLoading(button, isLoading) {
    if (isLoading) {
      button.disabled = true
      button.classList.add("loading", "loading-spinner")
      this.originalButtonText = button.textContent
      button.textContent = ""
    } else {
      button.disabled = false
      button.classList.remove("loading", "loading-spinner")
      if (this.originalButtonText) {
        button.textContent = this.originalButtonText
      }
    }
  }

  showSuccess(message) {
    this.messageTarget.classList.remove("hidden", "alert-error")
    this.messageTarget.classList.add("alert-success")
    this.messageTextTarget.textContent = message
  }

  showError(message) {
    this.messageTarget.classList.remove("hidden", "alert-success")
    this.messageTarget.classList.add("alert-error")
    this.messageTextTarget.textContent = message
  }

  hideMessage() {
    this.messageTarget.classList.add("hidden")
    this.messageTarget.classList.remove("alert-success", "alert-error")
  }

  getTranslation(key) {
    // This is a simple approach - in production you might want to pass these as data attributes
    const translations = {
      add_email: document.documentElement.lang === "pl" ? "Dodaj email" : "Add Email",
      add_phone: document.documentElement.lang === "pl" ? "Dodaj numer telefonu" : "Add Phone Number",
      enter_email: document.documentElement.lang === "pl" ? "Wprowadź swój adres email" : "Enter your email address",
      enter_phone: document.documentElement.lang === "pl" ? "Wprowadź swój numer telefonu" : "Enter your phone number",
      code_sent: document.documentElement.lang === "pl" ? "Kod weryfikacyjny wysłany! Sprawdź %{type}." : "Verification code sent! Check your %{type}.",
      code_verified: document.documentElement.lang === "pl" ? "Kod potwierdzony! Aktualizowanie danych kontaktowych..." : "Code verified! Updating your contact information..."
    }
    return translations[key] || key
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
